import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useV2Session } from "../context/V2SessionContext";

export default function V2CompleteRegistration() {
  const navigate = useNavigate();
  const {
    beginCompleteRegistration,
    completeRegistration,
    hasRequiredWeight,
    loading,
    needsPasswordSetup,
    user,
  } = useV2Session();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [checkingLink, setCheckingLink] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [updated, setUpdated] = useState(false);

  useEffect(() => {
    let alive = true;
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const tokenHash = url.searchParams.get("token_hash");
    const type = url.searchParams.get("type");
    if (!tokenHash && !code) {
      setCheckingLink(false);
      return undefined;
    }

    beginCompleteRegistration({
      tokenHash,
      type,
      code,
    }).then((result) => {
      if (!alive) return;
      if (!result.ok) {
        setError(result.error || "Invalid or expired link.");
      }
      url.searchParams.delete("token_hash");
      url.searchParams.delete("type");
      url.searchParams.delete("code");
      window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
      setCheckingLink(false);
    });

    return () => {
      alive = false;
    };
  }, [beginCompleteRegistration]);

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    const result = await completeRegistration({ password });
    setSubmitting(false);

    if (!result.ok) {
      setError(result.error || "Password setup failed.");
      return;
    }

    setUpdated(true);
    navigate(hasRequiredWeight ? "/dashboard" : "/settings", { replace: true });
  };

  if (loading || checkingLink) {
    return <AuthShell title="Email verified" notes="Loading your verified registration..." />;
  }

  if (!user || !needsPasswordSetup) {
    return (
      <AuthShell title="Invalid or expired link" notes={error || "Open the latest verification email or start registration again."}>
        <Link to="/register">
          <Button type="button">Register</Button>
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={updated ? "Password updated" : "Set your password"} notes="Email verified. Choose a password to finish your account.">
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="v2-complete-password">Password</Label>
          <Input
            id="v2-complete-password"
            type="password"
            autoComplete="new-password"
            minLength={6}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              setError("");
            }}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="v2-complete-confirm">Confirm password</Label>
          <Input
            id="v2-complete-confirm"
            type="password"
            autoComplete="new-password"
            minLength={6}
            value={confirmPassword}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
              setError("");
            }}
            required
          />
        </div>
        {error && <p className="text-sm font-medium text-destructive">{error}</p>}
        <Button type="submit" disabled={submitting}>
          {submitting ? "Saving password..." : "Set password"}
        </Button>
      </form>
    </AuthShell>
  );
}

function AuthShell({ title, notes, children }) {
  return (
    <main className="min-h-screen bg-background px-4 py-12 text-foreground">
      <section className="mx-auto max-w-xl rounded-lg border border-border bg-card p-6">
        <h1 className="text-3xl font-extrabold">{title}</h1>
        <p className="mt-3 text-muted-foreground">{notes}</p>
        {children && <div className="mt-4">{children}</div>}
      </section>
    </main>
  );
}
